import * as flatbuffers from 'flatbuffers'
import { tbs } from './engine/bindings/typescript/engine'
import type { EngineExports } from './engine/bindings/typescript/engine_exports'

const debugWasmUrl = new URL('./engine/bin/engine_debug.wasm', import.meta.url)
const releaseWasmUrl = new URL('./engine/bin/engine.wasm', import.meta.url)

const wasmConfig = {
  url: import.meta.env.DEV ? debugWasmUrl : releaseWasmUrl,
  label: import.meta.env.DEV ? 'debug' : 'release',
}

const ENGINE_RESULT_WORDS = 4
const ENGINE_RESULT_BYTES = ENGINE_RESULT_WORDS * 4

// Handwritten frontend bridge over downloaded wasm binaries and generated
// FlatBuffer bindings. Raw export declarations come from generated engine
// artifacts; wrapper loading, decoding, and error policy remain handwritten.
// Stronger runtime ABI validation is intentionally deferred.

export interface EngineStateSnapshot {
  bytes: Uint8Array
  length: number
  payloadType: tbs.EnginePayload
  payloadName: string
  message: tbs.EngineMessage
  messageObject: tbs.EngineMessageT
}

export interface EngineCallResult {
  code: number
  snapshot: EngineStateSnapshot | null
}

type EngineResultStruct = {
  code: number
  ptr: number
  len: number
}

async function fetchEngineBinary(url: URL): Promise<ArrayBuffer> {
  if (import.meta.env.MODE === 'test') {
    const { readFile } = await import('node:fs/promises')
    const { resolve } = await import('node:path')
    const filePath = resolve(
      process.cwd(),
      'src/engine/bin',
      import.meta.env.DEV ? 'engine_debug.wasm' : 'engine.wasm'
    )
    const wasmBinary = await readFile(filePath)
    return wasmBinary.buffer.slice(
      wasmBinary.byteOffset,
      wasmBinary.byteOffset + wasmBinary.byteLength
    )
  }

  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`[engine-wrapper] failed to download ${wasmConfig.label} wasm (${response.status} ${response.statusText})`)
  }
  return response.arrayBuffer()
}

export class EngineInstance {
  private constructor(private readonly exports: EngineExports) { }

  static async create(): Promise<EngineInstance> {
    console.info(`[engine-wrapper] loading ${wasmConfig.label} wasm from ${wasmConfig.url.href}`)
    const wasmBinary = await fetchEngineBinary(wasmConfig.url)
    // eslint-disable-next-line prefer-const
    let instanceRef: WebAssembly.Instance
    const { instance } = await WebAssembly.instantiate(wasmBinary, {
      env: {
        console_log: (ptr: number, len: number) => {
          const memory = instanceRef.exports.memory as WebAssembly.Memory
          const mem = new Uint8Array(memory.buffer, ptr, len)
          console.log(new TextDecoder().decode(mem))
        }
      },
    })
    instanceRef = instance
    return new EngineInstance(instance.exports as EngineExports)
  }

  runMapScript(inputBytes: Uint8Array = new Uint8Array()): EngineCallResult {
    const ptr = this.writeBytes(inputBytes)
    const result = this.withEngineResult((outPtr) => {
      this.exports.run_map_script(ptr, inputBytes.length, outPtr)
    })
    this.free(ptr, inputBytes.length)
    return result
  }

  createGameFromState(stateBytes: Uint8Array): number {
    const ptr = this.writeBytes(stateBytes)
    const { returnValue, ...result } = this.withEngineResult((outPtr) => (
      this.exports.create_game(ptr, stateBytes.length, outPtr)
    ))
    this.free(ptr, stateBytes.length)
    if (result.code !== 0) {
      throw this.createEngineError('create_game', result)
    }
    const handle = returnValue ?? 0
    if (handle <= 0) {
      throw new Error(`[engine-wrapper] create_game returned invalid handle ${handle}`)
    }
    return handle
  }

  getBlankStateForEngineInit(): EngineStateSnapshot {
    const result = this.withEngineResult((outPtr) => {
      this.exports.get_blank_state_for_engine_init(outPtr)
    })
    if (result.code !== 0) {
      throw this.createEngineError('get_blank_state_for_engine_init', result)
    }
    if (!result.snapshot) {
      throw new Error('[engine-wrapper] get_blank_state_for_engine_init returned empty response')
    }
    return result.snapshot
  }

  appendMapEditorPlayer(stateBytes: Uint8Array): EngineStateSnapshot {
    const ptr = this.writeBytes(stateBytes)
    const result = this.withEngineResult((outPtr) => {
      this.exports.append_map_editor_player(ptr, stateBytes.length, outPtr)
    })
    this.free(ptr, stateBytes.length)
    if (result.code !== 0) {
      throw this.createEngineError('append_map_editor_player', result)
    }
    if (!result.snapshot) {
      throw new Error('[engine-wrapper] append_map_editor_player returned empty response')
    }
    return result.snapshot
  }

  applyAction(handle: number, actionBytes: Uint8Array): EngineCallResult {
    const ptr = this.writeBytes(actionBytes)
    const result = this.withEngineResult((outPtr) => {
      this.exports.apply_action(handle, ptr, actionBytes.length, outPtr)
    })
    this.free(ptr, actionBytes.length)
    return result
  }

  getFullState(handle: number): EngineStateSnapshot {
    const result = this.withEngineResult((outPtr) => {
      this.exports.get_full_state(handle, outPtr)
    })
    if (result.code !== 0) {
      throw this.createEngineError('get_full_state', result)
    }
    if (!result.snapshot) {
      throw new Error('[engine-wrapper] get_full_state returned empty response')
    }
    return result.snapshot
  }

  private allocResultBuffer(): number {
    return this.allocBytes(ENGINE_RESULT_BYTES)
  }

  private readUint32(ptr: number): number {
    const view = new DataView(this.exports.memory.buffer)
    return view.getUint32(ptr, true)
  }

  private readBytes(ptr: number, len: number): Uint8Array {
    return new Uint8Array(this.exports.memory.buffer, ptr, len).slice()
  }

  private writeBytes(source: Uint8Array): number {
    const ptr = this.allocBytes(source.length)
    new Uint8Array(this.exports.memory.buffer, ptr, source.length).set(source)
    return ptr
  }

  private allocBytes(size: number): number {
    const ptr = this.exports.wasm_alloc(size)
    if (typeof ptr !== 'number' || ptr <= 0) {
      throw new Error(`[engine-wrapper] wasm_alloc failed for ${size} bytes`)
    }
    return ptr
  }

  private free(ptr: number, len: number): void {
    if (ptr <= 0 || len <= 0) {
      return
    }
    this.exports.wasm_free(ptr, len)
  }

  private withEngineResult(fn: (outPtr: number) => number | void): EngineCallResult & { returnValue?: number } {
    const outPtr = this.allocResultBuffer()
    new Uint8Array(this.exports.memory.buffer, outPtr, ENGINE_RESULT_BYTES).fill(0)
    const returnValue = fn(outPtr)
    const { code, ptr, len } = this.readEngineResult(outPtr)
    const bytes = ptr > 0 && len > 0 ? this.readBytes(ptr, len) : null
    this.free(outPtr, ENGINE_RESULT_BYTES)
    this.free(ptr, len)
    return {
      code,
      snapshot: bytes ? this.decodeMessage(bytes) : null,
      returnValue: typeof returnValue === 'number' ? returnValue : undefined,
    }
  }

  private readEngineResult(ptr: number): EngineResultStruct {
    return {
      code: this.readUint32(ptr),
      ptr: this.readUint32(ptr + 4),
      len: this.readUint32(ptr + 8),
    }
  }

  private createEngineError(exportName: string, result: EngineCallResult): Error {
    const payloadError = result.snapshot ? this.getSnapshotErrorMessage(result.snapshot) : null
    const message = payloadError
      ?? `engine error code ${result.code}`
    return new Error(`[engine-wrapper] ${exportName} failed: ${message}`)
  }

  private getSnapshotErrorMessage(snapshot: EngineStateSnapshot): string | null {
    const payload = snapshot.messageObject.payload
    if (payload instanceof tbs.ErrorEnvelopeT) {
      return String(payload.message ?? payload.name ?? `Engine error code ${payload.code}`)
    }
    return null
  }

  private decodeMessage(bytes: Uint8Array): EngineStateSnapshot {
    const message = tbs.EngineMessage.getRootAsEngineMessage(new flatbuffers.ByteBuffer(bytes))
    const payloadType = message.payloadType()
    const payloadName = tbs.EnginePayload[payloadType] ?? `payload-${payloadType}`
    return {
      bytes,
      length: bytes.length,
      payloadType,
      payloadName,
      message,
      messageObject: message.unpack(),
    }
  }
}
